import productLabels from 'i18n/productLabels'
import HotelListingSearchFilters from './HotelListingSearchFilters'
import { Fragment, useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import FilterChip from 'LuxKit/Chips/FilterChip'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import TripDrawer from 'tripPlanner/components/GenericDrawer/TripDrawer'
import TripDrawerHeaderTitle from 'tripPlanner/components/GenericDrawer/TripDrawerHeaderTitle'
import ListingDrawerHeaderActions from '../ListingDrawerHeaderActions'
import DrawerHotelTile from './DrawerHotelTile'
import HotelListingMapPreview from './HotelListingMapPreview'
import useHotelListingMap from './useHotelListingMap'
import DrawerHotelTileSkeleton from './DrawerHotelTileSkeleton'
import HotelListingEmptyState from './HotelListingEmptyState'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import HotelDetailView from './HotelDetailView'
import type { DrawerDates } from './useNotifyDatesChange'
import type { HotelListingDrawerProps } from './types'
import { formatDrawerHotelInfo } from './formatDrawerHotelInfo'
import AddHotelToTripButton from 'tripPlanner/components/AddToTrip/AddHotelToTripButton'
import { isOfferBookedInItinerary } from 'tripPlanner/utils/chatItinerary'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import HotelListingSearchControls, { type HotelListingSearchControlsHandle } from './HotelListingSearchControls'
import { HOTEL_DRAWER_MAX_GUESTS } from './constants'
import useHotelListingDrawerData, {
  HOTEL_DRAWER_MAX_WIDTH_PX,
} from './useHotelListingDrawerData'
import {
  shouldRenderTripDrawerTitleInContent,
  useRightPanelTripDrawerPresentation,
} from 'tripPlanner/components/GenericDrawer/TripDrawerPresentationContext'
import TripDrawerAssistantBridge, { TripDrawerAssistantFooter } from '../TripDrawerAssistantBridge'
import { formatTripDrawerMapLabel } from '../formatTripDrawerMapLabel'
import { buildHotelSearchDrawerContext } from '../../utils/drawerAskAIContext'
import { ContentWrapper } from './HotelListingDrawer.styles'
import { isStandardOccupancy } from 'lib/search/searchUtils'
import { buildSelectDatesSearchParams } from 'lib/search/selectDatesUtils'

export { ContentWrapper } from './HotelListingDrawer.styles'

const NonInteractiveChipWrapper = styled.div`
  pointer-events: none;
`

const SKELETON_COUNT = 3

const FullWidthLoadingBox = styled(LoadingBox)`
  width: 100%;
`

function HotelListingDrawer({
  isOpen,
  onClose,
  placeName,
  placeCountry,
  placeId,
  seedSearchFromPlace,
  initialSearchParams,
  stopStartDate,
  stopEndDate,
  stopCoordinates,
  contextStop,
  onShowMap,
  itineraryData,
  onSendMessage,
  initialSelectedOfferId,
  initialSelectedOfferType,
  onBack,
  onAfterClose,
  animation,
}: HotelListingDrawerProps) {
  const intl = useIntl()
  const drawerPresentation = useRightPanelTripDrawerPresentation()
  const {
    applyFilters,
    applyHotelDates,
    applyHotelRooms,
    browseAllResultsUrl,
    canGoBack,
    canShowExploreSection,
    checkIn,
    checkOut,
    dayRangeLabel,
    detailHotel,
    drawerSessionKey,
    exploreHeading,
    filterNights,
    clearFilters,
    filterOptions,
    filters,
    flexibleMonths,
    flexibleNights,
    handleAfterClose,
    handleBack,
    handleClose,
    handleSearchSubmit,
    handleSelectHotel,
    hasHotels,
    hasMore,
    hasSavedHotels,
    hasUnresolvedDestinationSearch,
    headerTitle,
    hotelDateSelection,
    isBooked,
    isDetailError,
    isDetailLoading,
    isDetailView,
    isLoadMoreError,
    isLoading,
    isLoadingMore,
    isSearchError,
    listingScrollResetKey,
    loadMore,
    loadMoreTriggerRef,
    occupancies,
    orderedHotelSearchSections,
    orderedHotelsWithTileDetails,
    orderIdForLink,
    refetchDetail,
    refetchSearch,
    reviews,
    reviewsLoading,
    savedBookedRooms,
    savedBooking,
    savedDrawerHotels,
    savedHeading,
    searchCalendarFocusDate,
    searchLocation,
    searchQuery,
    selectedHotelTitle,
    selectedOfferId,
    selectedSearchItem,
    tracking,
    trackingOfferById,
  } = useHotelListingDrawerData({
    isOpen,
    onClose,
    placeName,
    placeCountry,
    placeId,
    seedSearchFromPlace,
    initialSearchParams,
    stopStartDate,
    stopEndDate,
    contextStop,
    itineraryData,
    initialSelectedOfferId,
    initialSelectedOfferType,
  })
  const { mapHotels, coordinates: hotelMapCoordinates, onViewOnMap } = useHotelListingMap({
    hotels: orderedHotelsWithTileDetails,
    savedHotels: savedDrawerHotels,
    isLoading,
    isError: isSearchError || hasUnresolvedDestinationSearch,
    searchQuery,
    stopCoordinates,
    placeName,
    placeCountry,
    stopId: contextStop?.id,
    onClose: handleClose,
  })
  const searchControlsRef = useRef<HotelListingSearchControlsHandle>(null)
  const hasStandardSearchOccupancy = useMemo(
    () => isStandardOccupancy({ rooms: occupancies }),
    [occupancies],
  )
  const hotelFilters = useMemo(() => (
    <HotelListingSearchFilters filters={filters} options={filterOptions} onChange={applyFilters} onClear={clearFilters} />
  ), [filterOptions, filters, clearFilters, applyFilters])
  const handleSelectDates = useCallback((offer: App.HotelOffer | App.BedbankOffer) => {
    const { propertySearchItem } = buildSelectDatesSearchParams(offer)
    searchControlsRef.current?.openHotelDatePicker(propertySearchItem)
  }, [])

  // The room-options picker keeps its dates in a component-local reducer and reports them
  // up via onDatesChange (see useNotifyDatesChange), the same channel ChatOfferDetailDrawer
  // owns. Unlike that drawer this one stays mounted across every hotel the user opens, so
  // the override is tagged with the offer it came from and ignored once another is selected.
  const detailOfferId = detailHotel?.offerId
  const [pickerOverride, setPickerOverride] = useState<{ offerId: string, dates: DrawerDates }>()
  const handleDatesChange = useCallback((dates: DrawerDates) => {
    if (!detailOfferId) return
    setPickerOverride({ offerId: detailOfferId, dates })
  }, [detailOfferId])

  const activeOverride = pickerOverride?.offerId === detailOfferId ? pickerOverride?.dates : undefined
  const liveCheckIn = activeOverride?.checkIn ?? checkIn
  const liveCheckOut = activeOverride?.checkOut ?? checkOut

  const handleShowMapFromHeader = useCallback(() => {
    if (detailHotel && onShowMap) {
      onShowMap(detailHotel)
    }
  }, [onShowMap, detailHotel])

  const handleRetrySearch = useCallback(() => {
    refetchSearch()
  }, [refetchSearch])

  // The drawer's own reset first, then the owner's -- a stacked drawer's owner
  // pops it off the stack here, which unmounts everything below.
  const handleDrawerAfterClose = useCallback(() => {
    handleAfterClose()
    onAfterClose?.()
  }, [handleAfterClose, onAfterClose])

  const listHeaderTitle = intl.formatMessage(productLabels.hotels)

  const shouldRenderTitleInContent =
    shouldRenderTripDrawerTitleInContent(drawerPresentation)
  const shouldRenderDetailTitleInContent =
    isDetailView && shouldRenderTitleInContent
  const shouldRenderListTitleInContent =
    !isDetailView && shouldRenderTitleInContent
  const hotelSearchSectionByPosition = useMemo(() => {
    return new Map(orderedHotelSearchSections.map((section) => [section.position, section]))
  }, [orderedHotelSearchSections])
  const nearbyOffersHeading = intl.formatMessage({
    id: '-DXsf5HRceqn854D3Yad',
    defaultMessage: 'Nearby offers',
    description:
      'Section heading in the trip planner hotel drawer before hotel offers near but outside the exact searched destination.',
  })

  const drawerHeader = useMemo(() => {
    if (shouldRenderDetailTitleInContent || shouldRenderListTitleInContent) return undefined

    const title = isDetailView ? selectedHotelTitle : listHeaderTitle
    if (!title) return undefined

    const subtitle = isDetailView ? detailHotel?.location : undefined

    return <TripDrawerHeaderTitle title={title} subtitle={subtitle} />
  }, [isDetailView, listHeaderTitle, selectedHotelTitle, detailHotel?.location, shouldRenderDetailTitleInContent, shouldRenderListTitleInContent])

  const listHeaderActions = isDetailView ? undefined : (
    <ListingDrawerHeaderActions
      showBrowseAllAction={hasHotels && !isSearchError && !hasUnresolvedDestinationSearch}
      browseAllResultsUrl={browseAllResultsUrl}
      vertical="hotel"
    />
  )

  const detailHeaderActions = useMemo(() => {
    if (!isDetailView || !detailHotel) return undefined
    const hasGeo = !!detailHotel.geoLocation
    const showMapLabel = formatTripDrawerMapLabel(intl, drawerPresentation)
    return (
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {hasGeo && onShowMap && (
          <FilterChip
            size="small"
            startIcon={<LineMapIcon />}
            onClick={handleShowMapFromHeader}
          >
            {showMapLabel}
          </FilterChip>
        )}
        {isBooked && dayRangeLabel && (
          <NonInteractiveChipWrapper>
            <FilterChip size="small">{dayRangeLabel}</FilterChip>
          </NonInteractiveChipWrapper>
        )}
        {onSendMessage && !isBooked && (
          <AddHotelToTripButton
            itineraryData={itineraryData}
            hotelName={detailHotel.name}
            offerId={detailHotel.offerId}
            offerType={detailHotel.offerType}
            hotelCity={detailHotel.location}
            contextStop={contextStop}
            onSendMessage={onSendMessage}
            checkIn={liveCheckIn}
            checkOut={liveCheckOut}
            variant="outline"
            size="small"
          />
        )}
      </Group>
    )
  }, [
    isDetailView,
    detailHotel,
    drawerPresentation,
    handleShowMapFromHeader,
    dayRangeLabel,
    intl,
    onSendMessage,
    isBooked,
    itineraryData,
    contextStop,
    liveCheckIn,
    liveCheckOut,
    onShowMap,
  ])

  const assistantHotelDetails =
    isDetailView && detailHotel
      ? formatDrawerHotelInfo(detailHotel)
      : undefined
  const assistantHotelSearchDetails = !isDetailView
    ? buildHotelSearchDrawerContext({
        isLoading,
        filters,
        hasHotels,
        searchLocation,
        checkIn,
        checkOut,
        flexibleMonths,
        flexibleNights,
        hotelDateSelection,
        intl,
        occupancies,
        savedDrawerHotels,
        orderedHotelsWithTileDetails,
      })
    : undefined
  const assistantContextLabel = isDetailView ? detailHotel?.name : (headerTitle ?? listHeaderTitle)
  const assistantContextDetails = isDetailView ? assistantHotelDetails : assistantHotelSearchDetails
  const contentScrollResetKey = isDetailView ? selectedOfferId : listingScrollResetKey

  return (
    <TripDrawer
      isOpen={isOpen}
      onClose={handleClose}
      onAfterClose={handleDrawerAfterClose}
      onBack={canGoBack ? handleBack : onBack}
      animation={animation}
      direction="right-to-left"
      presentation={drawerPresentation}
      contentScrollResetKey={contentScrollResetKey}
      header={drawerHeader}
      footer={
        <TripDrawerAssistantFooter
          presentation={drawerPresentation}
          contextLabel={isDetailView ? detailHotel?.name : undefined}
          isOpen={isOpen}
          contextDetails={assistantHotelDetails}
        />
      }
      headerActions={isDetailView ? detailHeaderActions : listHeaderActions}
      maxWidth={HOTEL_DRAWER_MAX_WIDTH_PX}
    >
      <TripDrawerAssistantBridge
        presentation={drawerPresentation}
        isOpen={isOpen}
        contextLabel={assistantContextLabel}
        contextDetails={assistantContextDetails}
      />
      {isDetailView && (
        <>
          {isDetailLoading && !detailHotel && (
            <ContentWrapper>
              <Group direction="vertical" gap={16}>
                <FullWidthLoadingBox height={200} borderRadius="M" />
                {shouldRenderDetailTitleInContent && selectedHotelTitle ? (
                  <Heading variant="heading3">{selectedHotelTitle}</Heading>
                ) : (
                  <LoadingBox height={28} width={240} borderRadius="S" />
                )}
                <LoadingBox height={20} width={160} borderRadius="S" />
              </Group>
            </ContentWrapper>
          )}
          {isDetailError && !isDetailLoading && !detailHotel && (
            <ContentWrapper>
              <Group
                direction="vertical"
                gap={8}
                horizontalAlign="center"
                verticalAlign="center"
              >
                {shouldRenderDetailTitleInContent && selectedHotelTitle && (
                  <Heading variant="heading3">{selectedHotelTitle}</Heading>
                )}
                <BodyText variant="medium" colour="critical">
                  {intl.formatMessage({
                    id: 'AyMdUB-fKPPyurSXTes2',
                    defaultMessage: 'Failed to load hotel details.',
                    description:
                      'Error message shown when hotel offer details fail to load in the drawer',
                  })}
                </BodyText>
                <TextButton
                  kind="secondary"
                  size="small"
                  onClick={() => refetchDetail()}
                >
                  {intl.formatMessage({
                    id: 'q2nIV5-Sco41UZtxNQNg',
                    defaultMessage: 'Try again',
                    description:
                      'Button label to retry loading hotel offer details after an error',
                  })}
                </TextButton>
              </Group>
            </ContentWrapper>
          )}
          {detailHotel && (
            <HotelDetailView
              hotel={detailHotel}
              onEnlargeMap={
                detailHotel.geoLocation && onShowMap ? handleShowMapFromHeader : undefined
              }
              titleInContent={shouldRenderDetailTitleInContent ? detailHotel.name : undefined}
              showLocationInContent={shouldRenderDetailTitleInContent}
              reviews={reviews}
              reviewsLoading={reviewsLoading}
              isBooked={isBooked}
              orderId={orderIdForLink}
              savedBookedRooms={savedBookedRooms}
              itineraryData={itineraryData}
              contextStop={contextStop}
              onSendMessage={onSendMessage}
              checkIn={savedBooking?.checkIn ?? checkIn}
              checkOut={savedBooking?.checkOut ?? checkOut}
              initialOccupancies={savedBooking?.occupancies ?? occupancies}
              selectedPackageId={savedBooking?.packageId}
              selectedRoomTypeId={savedBooking?.roomTypeId}
              selectedRateId={savedBooking?.rateId}
              selectedCheckIn={savedBooking?.checkIn}
              selectedCheckOut={savedBooking?.checkOut}
              selectedCharterOriginAirportCode={
                savedBooking?.charterOriginAirportCode
              }
              selectedCharterFareClass={savedBooking?.charterFareClass}
              onDatesChange={handleDatesChange}
            />
          )}
        </>
      )}

      {!isDetailView && (
        <ContentWrapper>
          <Group direction="vertical" gap={12} tabletGap={24}>
            {shouldRenderListTitleInContent && (
              <Group direction="vertical" gap={4}>
                <Heading variant="heading3">{listHeaderTitle}</Heading>
                <CSSBreakpoint min="tablet">
                  <BodyText variant="large">{searchLocation}</BodyText>
                </CSSBreakpoint>
              </Group>
            )}

            <Group direction="vertical" gap={8}>
              <HotelListingSearchControls
                hotelFilters={hotelFilters}
                ref={searchControlsRef}
                key={drawerSessionKey}
                searchLabel={intl.formatMessage({
                  id: 'FuWeW8ek881Mgae0uRy4',
                  defaultMessage: 'Search hotels',
                  description:
                    'Label for the hotel search input in the trip planner hotel drawer',
                })}
                searchValue={searchLocation}
                searchPlaceholder={intl.formatMessage({
                  id: '3ly94lLroqBfy-zQUlLB',
                  defaultMessage: 'Search hotels or destinations',
                  description:
                    'Placeholder for the hotel search input in the trip planner hotel drawer',
                })}
                onSearchSubmit={handleSearchSubmit}
                searchItem={selectedSearchItem}
                hotelDateSelection={hotelDateSelection}
                onHotelDateSelectionChange={applyHotelDates}
                rooms={occupancies}
                maxGuests={HOTEL_DRAWER_MAX_GUESTS}
                onRoomsChange={applyHotelRooms}
                searchCalendarFocusDate={searchCalendarFocusDate}
                tripStops={itineraryData?.stops}
                contextStopId={contextStop?.id}
              />
              <Group direction="vertical" gap={24}>

                {hotelMapCoordinates && (
                  <HotelListingMapPreview
                    hotels={mapHotels}
                    coordinates={hotelMapCoordinates}
                    onSelectHotel={handleSelectHotel}
                    onViewOnMap={onViewOnMap}
                    itineraryData={itineraryData}
                    onSendMessage={onSendMessage}
                  />
                )}

                {hasSavedHotels && (
                  <Group direction="vertical" gap={12}>
                    <Heading variant="heading4">{savedHeading}</Heading>
                    <OfferListEventsProvider tracking={tracking}>
                      <Group direction="vertical" gap={16}>
                        {savedDrawerHotels.map((hotel, index) => (
                          <DrawerHotelTile
                            key={hotel.id}
                            hotel={hotel}
                            isBooked={isOfferBookedInItinerary(
                              hotel.offerId,
                              itineraryData,
                            )}
                            onSelectHotel={handleSelectHotel}
                            position={index}
                            trackingOffer={trackingOfferById.get(hotel.offerId)}
                            itineraryData={itineraryData}
                            contextStop={contextStop}
                            onSendMessage={onSendMessage}
                            onShowOnMap={onShowMap}
                            filterNights={filterNights}
                            checkIn={checkIn}
                            checkOut={checkOut}
                            roomCount={occupancies.length}
                          />
                        ))}
                      </Group>
                    </OfferListEventsProvider>
                  </Group>
                )}

                <Group direction="vertical" gap={16}>
                  {hasSavedHotels && canShowExploreSection && (
                    <Heading variant="heading4">{exploreHeading}</Heading>
                  )}

                  {isLoading && (
                    <Group direction="vertical" gap={16}>
                      {Array.from({ length: SKELETON_COUNT }, (_, index) => (
                        <DrawerHotelTileSkeleton key={index} />
                      ))}
                    </Group>
                  )}

                  {!isLoading && isSearchError && (
                    <Group
                      direction="vertical"
                      gap={8}
                      horizontalAlign="center"
                      verticalAlign="center"
                    >
                      <BodyText variant="medium" colour="critical">
                        {intl.formatMessage({
                          id: 'rbLgCIk9QtbOYXSL73Ck',
                          defaultMessage: 'Failed to load hotels.',
                          description:
                        'Error message shown when hotel search results fail to load in the trip planner drawer.',
                        })}
                      </BodyText>
                      <TextButton
                        kind="secondary"
                        size="small"
                        onClick={handleRetrySearch}
                      >
                        {intl.formatMessage({
                          id: 'Rwzku6SpP3n8bDdSs_wh',
                          defaultMessage: 'Try again',
                          description:
                        'Button label to retry loading hotel search results in the trip planner drawer.',
                        })}
                      </TextButton>
                    </Group>
                  )}

                  {!isLoading && !isSearchError && !hasHotels && (
                    <HotelListingEmptyState
                      placeName={placeName}
                      searchUrl={browseAllResultsUrl}
                    />
                  )}

                  {!isLoading && !isSearchError && canShowExploreSection && (
                    <>
                      <OfferListEventsProvider tracking={tracking}>
                        <Group direction="vertical" gap={16}>
                          {orderedHotelsWithTileDetails.map((hotel, index) => {
                            const isBooked = isOfferBookedInItinerary(hotel.offerId, itineraryData)

                            return (
                              <Fragment key={hotel.id}>
                                {hotelSearchSectionByPosition.get(index)?.type === 'nearby' && (
                                  <Heading variant="heading5">
                                    {nearbyOffersHeading}
                                  </Heading>
                                )}
                                <DrawerHotelTile
                                  hotel={hotel}
                                  isBooked={isBooked}
                                  onSelectHotel={handleSelectHotel}
                                  onSelectDates={isBooked ? undefined : handleSelectDates}
                                  isAnytimeSearch={!isBooked && hotelDateSelection.type === 'anytime'}
                                  hasStandardOccupancy={hasStandardSearchOccupancy}
                                  position={savedDrawerHotels.length + index}
                                  trackingOffer={trackingOfferById.get(hotel.offerId)}
                                  itineraryData={itineraryData}
                                  contextStop={contextStop}
                                  onSendMessage={onSendMessage}
                                  onShowOnMap={onShowMap}
                                  filterNights={filterNights ?? (
                                    hotelDateSelection.type === 'flexible'
                                      ? hotel.price?.nights
                                      : undefined
                                  )}
                                  checkIn={checkIn}
                                  checkOut={checkOut}
                                  roomCount={occupancies.length}
                                />
                              </Fragment>
                            )
                          })}
                        </Group>
                      </OfferListEventsProvider>

                      {isLoadingMore && (
                        <Group direction="vertical" gap={16}>
                          {Array.from({ length: 2 }, (_, index) => (
                            <DrawerHotelTileSkeleton
                              key={`loading-more-${index}`}
                            />
                          ))}
                        </Group>
                      )}

                      {isLoadMoreError && <BodyText variant="medium" colour="critical">{intl.formatMessage({
                        id: 'KjNMbpPKhSFKZRLH7cPQ',
                        defaultMessage: 'Could not load more hotels. Your loaded results are still available. Try again below.',
                        description: 'Inline error above the load more control after a later hotel result page fails to load',
                      })}
                      </BodyText>}
                      {hasMore && (
                        <Group
                          ref={loadMoreTriggerRef}
                          direction="horizontal"
                          horizontalAlign="center"
                        >
                          <TextButton
                            kind="secondary"
                            size="medium"
                            onClick={loadMore}
                            disabled={isLoadingMore}
                          >
                            {intl.formatMessage({
                              id: 'pXLqUwEQiAzarpNypTPc',
                              defaultMessage: 'Show more hotels',
                              description:
                            'Button label to load more hotel results in the listing drawer',
                            })}
                          </TextButton>
                        </Group>
                      )}
                    </>
                  )}
                </Group>
              </Group>
            </Group>
          </Group>
        </ContentWrapper>
      )}
    </TripDrawer>
  )
}

export default HotelListingDrawer
